/**
 * Performance Optimization Utilities
 * Provides various performance optimization techniques
 */

/**
 * Debounce function to limit function calls
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @param {boolean} immediate - Execute immediately
 * @returns {Function} Debounced function
 */
export function debounce(func, wait, immediate = false) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
}

/**
 * Throttle function to limit function calls
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function} Throttled function
 */
export function throttle(func, limit) {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Lazy load images with intersection observer
 * @param {string} selector - CSS selector for images
 * @param {Object} options - Intersection observer options
 */
export function lazyLoadImages(selector = 'img[data-src]', options = {}) {
  if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
    return;
  }

  const defaultOptions = {
    root: null,
    rootMargin: '50px',
    threshold: 0.1
  };

  const observerOptions = { ...defaultOptions, ...options };
  const imageObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const img = entry.target;
        if (img.dataset.src) {
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
          img.classList.add('loaded');
        }
        imageObserver.unobserve(img);
      }
    });
  }, observerOptions);

  const images = document.querySelectorAll(selector);
  images.forEach(img => imageObserver.observe(img));
}

/**
 * Preload critical resources
 * @param {Array} resources - Array of resource objects
 */
export function preloadResources(resources) {
  if (typeof window === 'undefined') return;

  resources.forEach(resource => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = resource.href;
    link.as = resource.as;
    
    if (resource.crossOrigin) {
      link.crossOrigin = resource.crossOrigin;
    }
    
    if (resource.as === 'style') {
      link.onload = () => {
        link.rel = 'stylesheet';
      };
    }
    
    document.head.appendChild(link);
  });
}

/**
 * Prefetch resources for better performance
 * @param {Array} urls - Array of URLs to prefetch
 */
export function prefetchResources(urls) {
  if (typeof window === 'undefined') return;

  urls.forEach(url => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.appendChild(link);
  });
}

/**
 * Optimize scroll performance
 * @param {Function} callback - Scroll callback function
 * @param {number} throttleMs - Throttle time in milliseconds
 */
export function optimizeScroll(callback, throttleMs = 16) {
  if (typeof window === 'undefined') return;

  const throttledCallback = throttle(callback, throttleMs);
  window.addEventListener('scroll', throttledCallback, { passive: true });
  
  return () => {
    window.removeEventListener('scroll', throttledCallback);
  };
}

/**
 * Optimize resize performance
 * @param {Function} callback - Resize callback function
 * @param {number} debounceMs - Debounce time in milliseconds
 */
export function optimizeResize(callback, debounceMs = 250) {
  if (typeof window === 'undefined') return;

  const debouncedCallback = debounce(callback, debounceMs);
  window.addEventListener('resize', debouncedCallback);
  
  return () => {
    window.removeEventListener('resize', debouncedCallback);
  };
}

/**
 * Create a performance mark
 * @param {string} name - Mark name
 */
export function mark(name) {
  if (typeof window !== 'undefined' && 'performance' in window && 'mark' in window.performance) {
    window.performance.mark(name);
  }
}

/**
 * Measure performance between two marks
 * @param {string} name - Measure name
 * @param {string} startMark - Start mark name
 * @param {string} endMark - End mark name
 */
export function measure(name, startMark, endMark) {
  if (typeof window !== 'undefined' && 'performance' in window && 'measure' in window.performance) {
    try {
      window.performance.measure(name, startMark, endMark);
    } catch (error) {
      console.warn('Performance measure failed:', error);
    }
  }
}

/**
 * Get performance timing
 * @param {string} name - Measure name
 * @returns {Object} Performance timing
 */
export function getTiming(name) {
  if (typeof window === 'undefined' || !('performance' in window)) {
    return null;
  }

  const measures = window.performance.getEntriesByName(name, 'measure');
  if (measures.length === 0) return null;

  const measure = measures[measures.length - 1];
  return {
    name: measure.name,
    duration: measure.duration,
    startTime: measure.startTime
  };
}

/**
 * Optimize DOM queries with caching
 * @param {string} selector - CSS selector
 * @returns {Function} Cached query function
 */
export function createCachedQuery(selector) {
  let cachedElement = null;
  let lastQuery = null;

  return function queryElement() {
    if (lastQuery !== selector) {
      cachedElement = document.querySelector(selector);
      lastQuery = selector;
    }
    return cachedElement;
  };
}

/**
 * Batch DOM updates for better performance
 * @param {Function} updateFunction - Function containing DOM updates
 */
export function batchDOMUpdates(updateFunction) {
  if (typeof window === 'undefined') return;

  // Use requestAnimationFrame for batching
  requestAnimationFrame(() => {
    updateFunction();
  });
}

/**
 * Optimize event listeners with delegation
 * @param {string} event - Event type
 * @param {string} selector - CSS selector for delegation
 * @param {Function} handler - Event handler
 * @param {Object} options - Event options
 */
export function delegateEvent(event, selector, handler, options = {}) {
  if (typeof window === 'undefined') return;

  const delegatedHandler = (e) => {
    if (e.target.matches(selector)) {
      handler(e);
    }
  };

  document.addEventListener(event, delegatedHandler, options);
  
  return () => {
    document.removeEventListener(event, delegatedHandler);
  };
}

/**
 * Initialize performance optimizations
 */
export function initPerformanceOptimizations() {
  // Lazy load images
  lazyLoadImages();
  
  // Preload critical resources (only if they exist)
  preloadResources([
    // { href: '/fonts/inter.woff2', as: 'font', crossOrigin: 'anonymous' },
    // { href: '/images/hero-bg.jpg', as: 'image' }
  ]);
  
  // Prefetch likely next resources (only if they exist)
  // Note: These endpoints don't exist, so we skip prefetching them
  // prefetchResources([
  //   '/api/addresses',
  //   '/api/services'
  // ]);
  
  console.log('Performance optimizations initialized');
}
